import { minutesToApiTime } from "@/lib/booking/time-slots";
import type {
  BookingMiscellaneousQueryParams,
  MiscellaneousMasterDataItem,
  ProtectionPackage,
} from "@/lib/types/miscellaneous";

const FALLBACK_INCLUSION_LABELS: Record<string, string> = {
  HAND_TROLLY: "Hand Trolley",
  EXTRA_100_KM: "Extra 100 km",
  CHILD_SEAT: "Child Seat",
  ADDITIONAL_DRIVER: "Additional Driver",
  WINDSCREEN_PROTECTION: "Windscreen Protection",
  ROAD_SIDE_ASSISTANCE: "Roadside Assistance",
  SNOW_COVER: "Snow Cover",
};

export type InclusionItem = {
  key: string;
  label: string;
};

const DEFAULT_API_TIME = "08:00";

export function parseBookingMiscellaneousParams(
  searchParams: URLSearchParams,
): BookingMiscellaneousQueryParams | null {
  const pickupLocation = Number(searchParams.get("pickup"));
  const returnLocation = Number(
    searchParams.get("return") ?? searchParams.get("pickup"),
  );
  const fromDate = searchParams.get("pickupDate");
  const toDate = searchParams.get("returnDate");
  const pickupMinutes = Number(searchParams.get("pickupTime"));
  const returnMinutes = Number(searchParams.get("returnTime"));
  const selectedVehicleTypeId = Number(
    searchParams.get("selectedVehicleTypeId") ??
      searchParams.get("selected_vehicle_type_id"),
  );
  const selectedPackage = searchParams.get("selectedPackage");

  if (
    Number.isNaN(pickupLocation) ||
    Number.isNaN(returnLocation) ||
    !fromDate ||
    !toDate ||
    Number.isNaN(selectedVehicleTypeId)
  ) {
    return null;
  }

  return {
    pickup_location: pickupLocation,
    return_location: returnLocation,
    from_date: fromDate,
    to_date: toDate,
    from_time: Number.isNaN(pickupMinutes)
      ? DEFAULT_API_TIME
      : minutesToApiTime(pickupMinutes),
    to_time: Number.isNaN(returnMinutes)
      ? DEFAULT_API_TIME
      : minutesToApiTime(returnMinutes),
    selected_vehicle_type_id: selectedVehicleTypeId,
    selected_package: selectedPackage || null,
  };
}

export function buildInclusionItems(
  miscellaneousMasterData: MiscellaneousMasterDataItem[],
  packages: ProtectionPackage[],
): InclusionItem[] {
  if (miscellaneousMasterData.length > 0) {
    return miscellaneousMasterData.map((item) => ({
      key: item.key,
      label: item.name,
    }));
  }

  const keys = new Set<string>();
  for (const pkg of packages) {
    for (const key of Object.keys(pkg.miscellaneous_included)) {
      keys.add(key);
    }
  }

  return Array.from(keys).map((key) => ({
    key,
    label: FALLBACK_INCLUSION_LABELS[key] ?? formatMiscKey(key),
  }));
}

export function getIncludedOverviewLabels(
  selectedPackage: ProtectionPackage | null,
  inclusionItems: InclusionItem[],
): string[] {
  if (!selectedPackage) {
    return [];
  }

  return inclusionItems
    .filter(({ key }) => selectedPackage.miscellaneous_included[key] === true)
    .map(({ label }) => label);
}

function formatMiscKey(key: string): string {
  return key
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}
